import { Column, Row, SingleScrollView, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C1Text, C3Text } from "../../../common/components/text/CustomText";
import { Theme } from "../../../common/Theme";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import QRCodeCard from "../components/QRCodeCard";
import { useEffect, useRef, useState } from "react";
import QRCodePrintModal from "../modals/QRCodePrintModal";
import StoreService from "../../../service/StoreService";
import QRPrintContent from "../components/QRPrintContent";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import styled from "styled-components";

const OffscreenArea = styled.div`
    position: fixed;
    left: -99999px;
    top: 0;
    pointer-events: none;
`;

function QRCodeTabContent({ storeId, storeName }) {
    const storeService = new StoreService();
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openPrintModal, setOpenPrintModal] = useState(false);

    const qrRefs = useRef([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleAllDownload = async () => {
        if (isDownloading || devices.length === 0) return;
        setIsDownloading(true);
        try {
            const zip = new JSZip();
            const imgFolder = zip.folder("qr_codes");

            const elements = qrRefs.current.filter(Boolean);
            const blobs = await Promise.all(
                elements.map(async (element) => {
                    const canvas = await html2canvas(element, {
                        useCORS: true,
                        scale: 2,
                    });
                    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
                })
            );

            blobs.forEach((blob, i) => {
                if (blob) {
                    const name = devices[i]?.modelNm || devices[i]?.deviceId || `qr_code_${i + 1}`;
                    imgFolder.file(`${name}.png`, blob);
                }
            });

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "qr_codes.zip");
        } catch (error) {
            console.error("QR 전체 다운로드 실패:", error);
            alert("QR 코드 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (!storeId) return;
        setIsLoading(true);
        storeService
            .getStoreDevice({ storeId })
            .then((result) => {
                setDevices(Array.isArray(result) ? result : []);
            })
            .catch((error) => {
                console.error("Error fetching devices for QR codes:", error);
                setDevices([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [storeId]);

    return (
        <Column padding={"14px 0"} alignItem={"flex-start"} width={"100%"}>
            <SizedBox height={"16px"} />
            <Row width={"100%"} alignItem={"flex-end"} justifyContent={"space-between"} padding={"0 0 0 18px"}>
                <C1Text>QR Code Generate</C1Text>
                <Row>
                    <SecondaryButton
                        icon={"folder_download"}
                        text={"All Download"}
                        onClick={handleAllDownload}
                        disabled={isDownloading || devices.length === 0}
                    />
                    <SizedBox width={"10px"} />
                    <SecondaryButton icon={"print"} text={"Print"} onClick={() => setOpenPrintModal(true)} />
                </Row>
            </Row>
            <SizedBox height={"12px"} />
            {isLoading ? null : devices.length > 0 ? (
                <SingleScrollView $gridTemplateColumns={"repeat(auto-fill, minmax(188px, 1fr))"} $gap={12}>
                    {devices.map((device, index) => (
                        <QRCodeCard key={device.deviceId || index} device={device} storeId={storeId} />
                    ))}
                </SingleScrollView>
            ) : (
                <Column width={"100%"} alignItem={"center"} justifyContent={"center"} padding={"80px 0"}>
                    <C3Text fontColor={Theme.color.gray500}>There are no registered devices.</C3Text>
                </Column>
            )}
            <OffscreenArea aria-hidden>
                {devices.map((device, index) => (
                    <QRPrintContent
                        key={device.deviceId || index}
                        ref={(el) => (qrRefs.current[index] = el)}
                        device={device}
                        storeId={storeId}
                        storeName={storeName}
                    />
                ))}
            </OffscreenArea>
            {openPrintModal && (
                <QRCodePrintModal
                    isOpen={openPrintModal}
                    onClose={() => setOpenPrintModal(false)}
                    devices={devices}
                    storeId={storeId}
                    storeName={storeName}
                />
            )}
        </Column>
    );
}

export default QRCodeTabContent;
